import { useEffect, useRef, useState, useCallback } from 'react';
import {
  Box,
  IconButton,
  Slider,
  Typography,
  Stack,
  Select,
  MenuItem,
  FormControl,
  Button,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  VolumeUp as VolumeUpIcon,
  VolumeOff as VolumeMuteIcon,
  Speed as SpeedIcon,
  AddCircleOutline as AddSegmentIcon,
} from '@mui/icons-material';
import WaveSurfer from 'wavesurfer.js';
import { useCreateSegment, useSegments } from '../../hooks/useSegments';
import { useTrackListeningTime } from '../../hooks/useListeningTime';
import { useTrackSegmentPlay } from '../../hooks/useLearningMetrics';
import { SegmentTimingForm } from '../youtube/SegmentTimingForm';

import type { Segment } from '../../types/api.types';

interface AudioPlayerProps {
  audioUrl: string;
  episodeName: string;
  projectId: number;
  episodeId: number;
  projectLanguage: string;
  /** Called after a segment is created, with the saved times so the parent can select/seek it. */
  onSegmentCreated?: (startTime: number, endTime: number) => void;
  onPlaySegmentRequest?: (handler: (start: number, end: number, loop: boolean, segmentId?: number) => void) => void;
  onSkipRequest?: (handler: (seconds: number) => void) => void;
  onPlaybackStateChange?: (isPlaying: boolean) => void;
  onTimeUpdate?: (currentTime: number) => void;
  /** When set, the form switches to edit mode pre-populated with this segment's times. */
  editingSegment?: Segment | null;
  /** Called when the user saves an edit — parent updates segment and seeks. */
  onUpdateSegment?: (startTime: number, endTime: number) => void;
  /** Called when the user cancels editing. */
  onCancelEdit?: () => void;
  /** Called when the user clicks Preview — parent seeks and plays bounded. */
  onPreviewSegment?: (startTime: number, endTime: number) => void;
  /** True while the update mutation is in flight. */
  isUpdatingSegment?: boolean;
}

export const AudioPlayer = ({
  audioUrl, episodeName, projectId, episodeId, projectLanguage,
  onSegmentCreated, onPlaySegmentRequest, onSkipRequest, onPlaybackStateChange, onTimeUpdate,
  editingSegment, onUpdateSegment, onCancelEdit, onPreviewSegment, isUpdatingSegment = false,
}: AudioPlayerProps) => {
  const waveformRef = useRef<HTMLDivElement>(null);
  const wavesurferRef = useRef<WaveSurfer | null>(null);
  // const loopIntervalRef = useRef<number | null>(null);
  const loopSegmentRef = useRef<{ start: number; end: number; enabled: boolean; segmentId?: number } | null>(null);
  const lastUpdateTimeRef = useRef<number>(0);
  
  // Listening time tracking
  const playStartTimeRef = useRef<number | null>(null);
  const accumulatedSecondsRef = useRef<number>(0);
  const trackingIntervalRef = useRef<number | null>(null);
  
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddingSegment, setIsAddingSegment] = useState(false);

  const { mutateAsync: createSegmentAsync, isPending: isSaving } = useCreateSegment();
  const { data: segments = [] } = useSegments(projectId, episodeId);
  const { mutate: trackListeningTime } = useTrackListeningTime();
  const trackSegmentPlay = useTrackSegmentPlay();

  // Always-current language ref so the stable interval can read it without being in its deps
  const projectLanguageRef = useRef(projectLanguage);
  useEffect(() => {
    projectLanguageRef.current = projectLanguage;
  }, [projectLanguage]);

  // Send accumulated listening time to backend
  const sendListeningTime = useCallback(() => {
    const seconds = Math.floor(accumulatedSecondsRef.current);
    if (seconds > 0) {
      console.log('[AudioPlayer] Sending listening time:', seconds, 'seconds for language:', projectLanguageRef.current);
      trackListeningTime({ seconds, language: projectLanguageRef.current }, {
        onSuccess: () => console.log('[AudioPlayer] Successfully tracked listening time'),
        onError: (error) => console.error('[AudioPlayer] Failed to track listening time:', error),
      });
      accumulatedSecondsRef.current = 0; // Reset after sending
    }
  }, [trackListeningTime]);

  // Stable 30-second interval — lives for the entire component lifetime so that short
  // looping segments (which fire pause/play on each loop) never reset the countdown.
  // The callback reads playStartTimeRef to determine whether the player is active.
  useEffect(() => {
    trackingIntervalRef.current = window.setInterval(() => {
      if (playStartTimeRef.current !== null) {
        const elapsed = (performance.now() - playStartTimeRef.current) / 1000;
        accumulatedSecondsRef.current += elapsed;
        playStartTimeRef.current = performance.now(); // reset checkpoint
        console.log('[AudioPlayer] Interval tick. Accumulated:', accumulatedSecondsRef.current, 'seconds');
        if (accumulatedSecondsRef.current >= 30) {
          sendListeningTime();
        }
      }
    }, 30000);

    return () => {
      if (trackingIntervalRef.current !== null) {
        clearInterval(trackingIntervalRef.current);
        trackingIntervalRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // Stable interval: mounts once. sendListeningTime reads language via ref.
  }, [sendListeningTime]);

  // Start tracking: capture the play start timestamp (interval is already running)
  const startTracking = useCallback(() => {
    if (playStartTimeRef.current === null) {
      console.log('[AudioPlayer] Starting listening time tracking');
      playStartTimeRef.current = performance.now();
    }
  }, []);

  // Stop tracking: accumulate elapsed time and flush to backend
  const stopTracking = useCallback(() => {
    if (playStartTimeRef.current !== null) {
      const elapsed = (performance.now() - playStartTimeRef.current) / 1000;
      accumulatedSecondsRef.current += elapsed;
      playStartTimeRef.current = null;
      console.log('[AudioPlayer] Stopping tracking. Total accumulated:', accumulatedSecondsRef.current, 'seconds');
    }
    // Send any accumulated time to backend
    sendListeningTime();
  }, [sendListeningTime]);

  // Cleanup and send tracking data on unmount
  useEffect(() => {
    // Setup beforeunload handler to track time when browser closes
    const handleBeforeUnload = () => {
      stopTracking();
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      stopTracking(); // Send remaining time on component unmount
    };
  }, [stopTracking]);

  // Expose playSegment function to parent with loop support
  const playSegmentHandler = useCallback((start: number, end: number, loop: boolean = false, segmentId?: number) => {
    if (wavesurferRef.current) {
      // Check for stop signal (start === -1)
      if (start === -1) {
        wavesurferRef.current.pause();
        loopSegmentRef.current = null;
        return;
      }

      // Free-play signal: play from current position without segment boundaries
      if (start === -2) {
        loopSegmentRef.current = null;
        wavesurferRef.current.play();
        return;
      }

      // Seek-only signal (start === -3): seek to end without playing.
      // end carries the target time in seconds.
      if (start === -3) {
        const dur = wavesurferRef.current.getDuration();
        if (dur > 0) wavesurferRef.current.seekTo(end / dur);
        wavesurferRef.current.pause();
        return;
      }

      // Release-boundary signal (start === -4): clear any armed segment boundary
      // without touching playback, so free play continues past segment ends.
      if (start === -4) {
        loopSegmentRef.current = null;
        return;
      }
      
      // Store loop info
      if (loop) {
        loopSegmentRef.current = { start, end, enabled: true, segmentId };
      } else {
        loopSegmentRef.current = { start, end, enabled: false, segmentId };
      }

      const currentDuration = wavesurferRef.current.getDuration();
      if (currentDuration > 0) {
        // If the player is already within the requested segment range, resume from current position.
        // But if the player is effectively at the end of the segment (within a small tail tolerance),
        // treat that as outside so pressing play restarts the segment from the beginning.
        const currentTime = wavesurferRef.current.getCurrentTime();
        const TOLERANCE_START = 0.25; // tolerance for being considered "inside" before start
        const TOLERANCE_END = 0.15; // tolerance near end to treat as "ended"
        const withinRange = currentTime >= (start - TOLERANCE_START) && currentTime <= (end - TOLERANCE_END);
        if (withinRange) {
          wavesurferRef.current.play();
        } else {
          // Seek to segment start and play
          wavesurferRef.current.seekTo(start / currentDuration);
          wavesurferRef.current.play();
        }
      }
    }
  }, []);

  // Expose skip function to parent
  const skipHandler = useCallback((seconds: number) => {
    if (wavesurferRef.current) {
      const currentDuration = wavesurferRef.current.getDuration();
      const currentTime = wavesurferRef.current.getCurrentTime();
      
      // Calculate new time, clamped to valid range
      let newTime = currentTime + seconds;
      newTime = Math.max(0, Math.min(newTime, currentDuration));
      
      // Seek to new position
      wavesurferRef.current.seekTo(newTime / currentDuration);
      
      // Update current time state immediately for better UX
      setCurrentTime(newTime);
    }
  }, []);

  useEffect(() => {
    if (onPlaySegmentRequest) {
      onPlaySegmentRequest(playSegmentHandler);
    }
  }, [onPlaySegmentRequest, playSegmentHandler]);

  useEffect(() => {
    if (onSkipRequest) {
      onSkipRequest(skipHandler);
    }
  }, [onSkipRequest, skipHandler]);

  useEffect(() => {
    if (!waveformRef.current) return;

    // Initialize WaveSurfer
    const wavesurfer = WaveSurfer.create({
      container: waveformRef.current,
      waveColor: '#ddd',
      progressColor: '#1976d2',
      cursorColor: '#1976d2',
      barWidth: 2,
      barRadius: 3,
      cursorWidth: 1,
      height: 80,
      barGap: 2,
    });

    wavesurferRef.current = wavesurfer;

    // Use proxy URL to avoid CORS issues
    const proxyUrl = `${import.meta.env.VITE_API_BASE_URL}/audio/proxy?url=${encodeURIComponent(audioUrl)}`;
    
    // Load audio
    wavesurfer.load(proxyUrl);

    // Event listeners
    const handleReady = () => {
      setDuration(wavesurfer.getDuration());
      setIsLoading(false);
    };

    const handleAudioProcess = () => {
      const currentTime = wavesurfer.getCurrentTime();
      const now = Date.now();
      
      // Throttle time updates to ~4fps instead of 60fps to reduce re-renders
      if (now - lastUpdateTimeRef.current > 250) {
        setCurrentTime(currentTime);
        if (onTimeUpdate) onTimeUpdate(currentTime);
        lastUpdateTimeRef.current = now;
      }
      
      // Check segment boundaries (always check, not throttled)
      if (loopSegmentRef.current) {
        const { start, end, enabled, segmentId } = loopSegmentRef.current;
        if (currentTime >= end) {
          if (enabled) {
            // Loop: seek back to start
            wavesurfer.seekTo(start / wavesurfer.getDuration());
            // Track each loop iteration for analytics
            if (segmentId) {
              console.log('[AudioPlayer] Tracking loop iteration:', segmentId);
              trackSegmentPlay.mutate({ segmentId }, {
                onSuccess: () => console.log('[AudioPlayer] Successfully tracked loop iteration'),
                onError: (error) => console.error('[AudioPlayer] Failed to track loop iteration:', error),
              });
            }
          } else {
            // No loop: pause at end
            wavesurfer.pause();
            loopSegmentRef.current = null;
          }
        }
      }
    };

    const handleInteraction = () => {
      setCurrentTime(wavesurfer.getCurrentTime());
    };

    const handlePlay = () => {
      setIsPlaying(true);
      startTracking(); // Start tracking listening time
      if (onPlaybackStateChange) onPlaybackStateChange(true);
        // Dispatch a global event so other components (like segment buttons) can sync
        try {
          window.dispatchEvent(new CustomEvent('player:playback', { detail: { isPlaying: true } }));
        } catch (e) {
          // ignore in non-browser test environments
        }
    };

    const handlePause = () => {
      setIsPlaying(false);
      stopTracking(); // Stop tracking and send accumulated time
      if (onPlaybackStateChange) onPlaybackStateChange(false);
      try {
        window.dispatchEvent(new CustomEvent('player:playback', { detail: { isPlaying: false } }));
      } catch (e) {}
      try {
        window.dispatchEvent(new Event('player:stopped'));
      } catch (e) {}
    };

    const handleFinish = () => {
      setIsPlaying(false);
      stopTracking(); // Stop tracking and send accumulated time
      if (onPlaybackStateChange) onPlaybackStateChange(false);
      try {
        window.dispatchEvent(new CustomEvent('player:playback', { detail: { isPlaying: false } }));
      } catch (e) {}
      try {
        window.dispatchEvent(new Event('player:stopped'));
      } catch (e) {}
    };

    wavesurfer.on('ready', handleReady);
    wavesurfer.on('audioprocess', handleAudioProcess);
    wavesurfer.on('interaction', handleInteraction);
    wavesurfer.on('play', handlePlay);
    wavesurfer.on('pause', handlePause);
    wavesurfer.on('finish', handleFinish);

    return () => {
      // Stop tracking if still playing
      if (playStartTimeRef.current !== null) {
        stopTracking();
      }
      
      // Clean up all event listeners
      wavesurfer.un('ready', handleReady);
      wavesurfer.un('audioprocess', handleAudioProcess);
      wavesurfer.un('interaction', handleInteraction);
      wavesurfer.un('play', handlePlay);
      wavesurfer.un('pause', handlePause);
      wavesurfer.un('finish', handleFinish);
      
      // Clear loop state
      loopSegmentRef.current = null;
      
      // Destroy wavesurfer instance
      wavesurfer.destroy();
      
      // Clear refs
      wavesurferRef.current = null;
    };
  }, [audioUrl]);

  const handlePlayPause = () => {
    if (wavesurferRef.current) {
      wavesurferRef.current.playPause();
    }
  };

  const handleVolumeChange = (_event: Event, newValue: number | number[]) => {
    const vol = newValue as number;
    setVolume(vol);
    if (wavesurferRef.current) {
      wavesurferRef.current.setVolume(vol);
      setIsMuted(vol === 0);
    }
  };

  const handleMuteToggle = () => {
    if (wavesurferRef.current) {
      if (isMuted) {
        wavesurferRef.current.setVolume(volume);
        setIsMuted(false);
      } else {
        wavesurferRef.current.setVolume(0);
        setIsMuted(true);
      }
    }
  };

  const handlePlaybackRateChange = (rate: number) => {
    setPlaybackRate(rate);
    if (wavesurferRef.current) {
      wavesurferRef.current.setPlaybackRate(rate);
    }
  };

  const handleAddSegmentClick = useCallback(() => {
    setIsAddingSegment(true);
  }, []);

  const handleCancelSegment = useCallback(() => {
    setIsAddingSegment(false);
  }, []);

  const handleSaveNewSegment = useCallback(async (startTime: number, endTime: number) => {
    try {
      await createSegmentAsync({ projectId, episodeId, startTime, endTime });
      setIsAddingSegment(false);
      if (onSegmentCreated) onSegmentCreated(startTime, endTime);
    } catch {
      // useCreateSegment.onError shows a toast; nothing additional needed here
    }
  }, [createSegmentAsync, projectId, episodeId, onSegmentCreated]);

  const hasNoSegments = segments.length === 0;

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Box sx={{ width: '100%', p: { xs: 1, sm: 2 }, position: 'relative' }}>
      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.8; transform: scale(1.05); }
          }
        `}
      </style>
      <Typography variant="h6" gutterBottom noWrap sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
        {episodeName}
      </Typography>

      {/* Loading Overlay */}
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 1,
            zIndex: 1,
          }}
        >
          <CircularProgress size={60} />
          <Typography variant="body1" color="white" sx={{ mt: 2 }}>
            Loading audio...
          </Typography>
        </Box>
      )}

      {/* Waveform */}
      <Box
        ref={waveformRef}
        data-tour="waveform"
        sx={{
          mb: 2,
          touchAction: 'none', // Prevent browser default touch actions
          userSelect: 'none',
          WebkitUserSelect: 'none',
          '& canvas': {
            display: 'block',
          }
        }} 
      />

      {/* Time Display */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {formatTime(currentTime)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {formatTime(duration)}
        </Typography>
      </Box>

      {/* Controls */}
      <Stack 
        direction={{ xs: 'column', sm: 'row' }} 
        spacing={{ xs: 1, sm: 2 }} 
        alignItems={{ xs: 'stretch', sm: 'center' }}
      >
        {/* Primary Controls Row */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
          {/* Play/Pause */}
          <IconButton
            onClick={handlePlayPause}
            color="primary"
            size="large"
            disabled={isLoading}
          >
            {isPlaying ? <PauseIcon fontSize="large" /> : <PlayIcon fontSize="large" />}
          </IconButton>

          {/* Add Segment Button */}
          {!isAddingSegment && !editingSegment && (
            <Tooltip title="Add a segment by specifying start and end times">
              <Button
                variant="outlined"
                startIcon={<AddSegmentIcon />}
                onClick={handleAddSegmentClick}
                disabled={isLoading}
                size="small"
                data-tour="add-segment"
                sx={{
                  display: { xs: 'none', sm: 'inline-flex' },
                  animation: hasNoSegments ? 'pulse 2s ease-in-out infinite' : 'none',
                }}
              >
                Add Segment
              </Button>
            </Tooltip>
          )}
        </Stack>

        {/* Volume Controls */}
        <Stack 
          direction="row" 
          spacing={1} 
          alignItems="center" 
          sx={{ display: { xs: 'none', md: 'flex' } }}
        >
          <IconButton onClick={handleMuteToggle} size="small">
            {isMuted || volume === 0 ? <VolumeMuteIcon /> : <VolumeUpIcon />}
          </IconButton>
          <Slider
            value={isMuted ? 0 : volume}
            onChange={handleVolumeChange}
            min={0}
            max={1}
            step={0.01}
            sx={{ width: 100 }}
            size="small"
          />
        </Stack>

        {/* Playback Speed */}
        <Stack direction="row" spacing={1} alignItems="center">
          <SpeedIcon sx={{ display: { xs: 'none', sm: 'inline' } }} />
          <FormControl size="small" sx={{ minWidth: 80 }}>
            <Select
              value={playbackRate}
              onChange={(e) => handlePlaybackRateChange(e.target.value as number)}
            >
              <MenuItem value={0.5}>0.5x</MenuItem>
              <MenuItem value={0.75}>0.75x</MenuItem>
              <MenuItem value={1}>1x</MenuItem>
              <MenuItem value={1.25}>1.25x</MenuItem>
              <MenuItem value={1.5}>1.5x</MenuItem>
              <MenuItem value={2}>2x</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Stack>

      {/* Mobile Add Segment Button */}
      {!isAddingSegment && !editingSegment && (
        <Box sx={{ display: { xs: 'block', sm: 'none' }, mt: 1 }}>
          <Button
            variant="outlined"
            startIcon={<AddSegmentIcon />}
            onClick={handleAddSegmentClick}
            disabled={isLoading}
            size="small"
            fullWidth
            data-tour="add-segment-mobile"
            sx={{
              animation: hasNoSegments ? 'pulse 2s ease-in-out infinite' : 'none',
            }}
          >
            Add Segment
          </Button>
        </Box>
      )}

      {/* Inline segment timing form — add mode or edit mode */}
      {(isAddingSegment || editingSegment != null) && (
        <SegmentTimingForm
          currentTime={currentTime}
          onSave={editingSegment ? (onUpdateSegment ?? (() => {})) : handleSaveNewSegment}
          onCancel={editingSegment ? (onCancelEdit ?? (() => setIsAddingSegment(false))) : handleCancelSegment}
          isSaving={editingSegment ? isUpdatingSegment : isSaving}
          initialStartTime={editingSegment?.startTime}
          initialEndTime={editingSegment?.endTime}
          onPreview={onPreviewSegment}
        />
      )}
    </Box>
  );
};
